/**
 * Frontend Analytics
 *
 * - `useAnalytics()` mounted once at the app root tracks `page.viewed` on
 *   every route change, debounced.
 * - `trackEvent()` is the low-level helper used for event-driven calls
 *   (e.g. logout). All failures are silent — analytics must never break
 *   the UI.
 *
 * The endpoint is `/api/v1/activity` (intentionally innocuous to avoid
 * ad-blocker false positives).
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "../services/api";

export type AnalyticsEventType = "user.logged_out" | "page.viewed";

export async function trackEvent(
  eventType: AnalyticsEventType,
  properties: Record<string, unknown> = {}
): Promise<void> {
  try {
    await apiClient.post("/api/v1/activity", {
      event_type: eventType,
      properties,
    });
  } catch {
    // Silent failure.
  }
}

const DEBOUNCE_MS = 250;

export function useAnalytics(): void {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
    }
    timer.current = window.setTimeout(() => {
      if (lastPath.current === location.pathname) return;
      const previous = lastPath.current;
      lastPath.current = location.pathname;
      void trackEvent("page.viewed", {
        path: location.pathname,
        referrer: previous ?? document.referrer ?? null,
      });
    }, DEBOUNCE_MS);

    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [location.pathname]);
}
